/**
 * @fileoverview Lazy Mermaid loader + render/export helpers shared by the
 * inline chat DiagramCard, the ```mermaid fence renderer, the canvas
 * DiagramNode and the print/export pipeline.
 *
 * Mermaid is ~1.5MB minified, so it is only ever dynamic-imported — never
 * add a static `import mermaid` anywhere in the app bundle.
 *
 * `htmlLabels` is disabled so labels render as plain SVG text: SVGs with
 * `<foreignObject>` labels draw blank/tainted when rasterised to a canvas in
 * some browsers, which would break PNG export and the canvas PDF path.
 */

type MermaidTheme = "default" | "dark";

let initializedTheme: MermaidTheme | null = null;
let renderSeq = 0;

export function isDarkMode(): boolean {
  return (
    typeof document !== "undefined" &&
    document.documentElement.classList.contains("dark")
  );
}

async function loadMermaid(theme: MermaidTheme) {
  const mermaid = (await import("mermaid")).default;
  if (initializedTheme !== theme) {
    mermaid.initialize({
      startOnLoad: false,
      // "strict" HTML-encodes labels — diagram code is model-authored, so
      // never loosen this.
      securityLevel: "strict",
      theme,
      flowchart: { htmlLabels: false },
      // classDiagram / stateDiagram share the flowchart renderer's labels;
      // htmlLabels also exists at the top level for those.
      htmlLabels: false,
    });
    initializedTheme = theme;
  }
  return mermaid;
}

export type MermaidRenderResult = { svg: string } | { error: string };

/**
 * Validate and render Mermaid source to an SVG string. Never throws — syntax
 * errors come back as `{ error }` so callers can show the message alongside
 * the raw source (per MOR-975's graceful-degradation requirement).
 */
export async function renderMermaidSvg(
  code: string,
  opts?: { theme?: MermaidTheme },
): Promise<MermaidRenderResult> {
  const theme = opts?.theme ?? (isDarkMode() ? "dark" : "default");
  const id = `morgan-mermaid-${++renderSeq}`;
  try {
    const mermaid = await loadMermaid(theme);
    // parse() first: it produces the useful syntax-error message without
    // mounting anything into the DOM.
    await mermaid.parse(code);
    const { svg } = await mermaid.render(id, code);
    return { svg };
  } catch (err) {
    // A failed render() can leave its scratch element behind.
    document.getElementById(id)?.remove();
    document.getElementById(`d${id}`)?.remove();
    return { error: err instanceof Error ? err.message : String(err) };
  }
}

/** Intrinsic pixel size of a Mermaid SVG, from its viewBox. */
export function measureMermaidSvg(svg: string): {
  width: number;
  height: number;
} {
  const viewBox = /viewBox="([\d.\s-]+)"/.exec(svg)?.[1]?.trim().split(/\s+/);
  const width = viewBox ? Math.ceil(parseFloat(viewBox[2])) : 800;
  const height = viewBox ? Math.ceil(parseFloat(viewBox[3])) : 600;
  return {
    width: Number.isFinite(width) && width > 0 ? width : 800,
    height: Number.isFinite(height) && height > 0 ? height : 600,
  };
}

/**
 * Rasterise a Mermaid SVG to a PNG data URL. `scale` 3 gives high-DPI output
 * that holds up in decks and printed PDFs. Returns the SVG's intrinsic CSS
 * size too so callers can emit correctly-proportioned <img> tags.
 */
export async function mermaidSvgToPngDataUrl(
  svg: string,
  scale = 3,
): Promise<{ dataUrl: string; width: number; height: number }> {
  const { width, height } = measureMermaidSvg(svg);
  // Mermaid emits width="100%"-style attributes; pin explicit dimensions so
  // the Image decodes at the intrinsic size.
  const sized = svg.replace(
    /<svg /,
    `<svg width="${width}" height="${height}" `,
  );
  const blob = new Blob([sized], { type: "image/svg+xml;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  try {
    const img = new Image();
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error("Could not decode diagram SVG"));
      img.src = url;
    });
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(width * scale));
    canvas.height = Math.max(1, Math.round(height * scale));
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas 2D context unavailable");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    return { dataUrl: canvas.toDataURL("image/png"), width, height };
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** Download helpers for the single-diagram PNG / SVG export actions. */
export async function downloadMermaidPng(code: string, filename: string) {
  const rendered = await renderMermaidSvg(code, { theme: "default" });
  if ("error" in rendered) throw new Error(rendered.error);
  const { dataUrl } = await mermaidSvgToPngDataUrl(rendered.svg);
  const { downloadBlob } = await import("@/utils/export/exportHtmlDocument");
  downloadBlob(await (await fetch(dataUrl)).blob(), `${filename}.png`);
}

export async function downloadMermaidSvgFile(code: string, filename: string) {
  const rendered = await renderMermaidSvg(code, { theme: "default" });
  if ("error" in rendered) throw new Error(rendered.error);
  const { downloadBlob } = await import("@/utils/export/exportHtmlDocument");
  downloadBlob(
    new Blob([rendered.svg], { type: "image/svg+xml;charset=utf-8" }),
    `${filename}.svg`,
  );
}

/** Filesystem-safe filename stem from a diagram title. */
export function diagramFileStem(title: string): string {
  const stem = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return stem || "diagram";
}
